import SearchIcon from "@assets/icons/search.svg";
import CloseRoundedIcon from "@assets/icons/close-icon-rounded.svg";
import SelectorIcon from "@assets/icons/selector.svg";

import { autoUpdate, useFloating } from "@floating-ui/react";
import type { FetchNextPageOptions, InfiniteQueryObserverResult } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import type { ControlProps, GroupBase, OptionProps, StylesConfig } from "react-select";
import Select, { components } from "react-select";

import { APP_COLORS } from "@styles/colors";
import { ICompany } from "@interfaces/company.interface";
import { CompanyIconBadge } from "@components/AddNewEntity/Shared/CompanyIconBadge";
import { ICompanySelectOption } from "@components/AddNewEntity/Deal";
import { cn } from "@utils/tailwind.util";
import { useMaxMenuHeight } from "@components/AddNewEntity/hooks/useMaxMenuHeight";
import useSystemStore from "@store/useSystemStore";

export type SourceCompanyOption = Partial<ICompany> & {
  value?: string;
  label?: string;
  logo_url?: string;
  website?: string;
  data?: ICompanySelectOption;
};

interface SourceCompanySelectProps {
  value?: SourceCompanyOption | null;
  options: (SourceCompanyOption | GroupBase<SourceCompanyOption>)[] | null;
  placeholder: string;
  onValueChange: (value: SourceCompanyOption | null) => void;
  debouncedSearch: (inputValue: string) => void;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  hasNextPage: boolean;
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<any[], Error>>;
}

const MAX_MENU_HEIGHT = 400;

const getCustomStyles = (
  isDarkMode: boolean,
  maxMenuHeight: number
): StylesConfig<SourceCompanyOption, false> => {
  return {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused
        ? isDarkMode
          ? APP_COLORS.stroke.actionPrimaryDark
          : APP_COLORS.stroke.actionPrimary
        : isDarkMode
          ? APP_COLORS.stroke.primaryDark
          : APP_COLORS.stroke.primary,
      ":hover": {
        borderColor: isDarkMode ? APP_COLORS.stroke.secondaryDark : APP_COLORS.stroke.secondary,
      },
      borderWidth: 1,
      borderRadius: "8px 8px 0px 0px",
      boxShadow: state.isFocused ? "0px 0px 0px 3px #B9D6D1" : "none",
      backgroundColor: isDarkMode
        ? APP_COLORS.background.primaryDark
        : APP_COLORS.background.primary,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      fontSize: 14,
      margin: "3px",
      overflow: "hidden",
      whiteSpace: "nowrap",
    }),
    menu: (provided) => ({
      ...provided,
      // Above the form's sticky footer (z-[1]), which would otherwise hide the menu.
      zIndex: 2,
      boxShadow: "none",
      marginTop: "0px",
      marginBottom: "0px",
      position: "relative",
      width: "100%",
      borderWidth: 1,
      borderRadius: "0 0 8px 8px",
      borderColor: isDarkMode ? APP_COLORS.stroke.primaryDark : APP_COLORS.stroke.primary,
      backgroundColor: isDarkMode
        ? APP_COLORS.background.primaryDark
        : APP_COLORS.background.primary,
    }),
    option: (provided, { isSelected, isFocused }) => ({
      ...provided,
      backgroundColor:
        isSelected || isFocused
          ? isDarkMode
            ? APP_COLORS.background.primaryDark
            : APP_COLORS.background.primary
          : "transparent",
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      ":hover": {
        backgroundColor: isDarkMode
          ? APP_COLORS.background.secondaryDark
          : APP_COLORS.background.secondary,
        color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
      backgroundColor: "transparent",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: APP_COLORS.gray["400"],
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    }),
    menuList: (provided) => ({
      ...provided,
      borderTop: "none",
      width: "100%",
      overflowY: "auto",
      overflowX: "hidden",
      paddingTop: 0,
      paddingBottom: 0,
      maxHeight: maxMenuHeight,
      maxWidth: "100%",
      backgroundColor: isDarkMode
        ? APP_COLORS.background.primaryDark
        : APP_COLORS.background.primary,
    }),
    clearIndicator: (provided) => ({
      ...provided,
      marginRight: "4px",
    }),
    group: (provided) => ({
      ...provided,
      paddingBottom: "0px",
    }),
    container: () => ({
      width: "100%",
    }),
  };
};

const CustomOption = ({
  optionProps,
}: {
  optionProps: JSX.IntrinsicAttributes &
    OptionProps<SourceCompanyOption, false, GroupBase<SourceCompanyOption>>;
}) => {
  const { isDarkMode } = useSystemStore();
  const { label, website } = optionProps.data;
  const textColor = isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary;

  return (
    <components.Option {...optionProps}>
      <div
        className={cn(
          "custom-option flex items-center justify-between gap-5 rounded-md px-2 py-2 text-sm font-medium"
        )}
        style={{
          backgroundColor: "transparent",
          color: textColor,
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <CompanyIconBadge url={optionProps.data.logo_url} />
          <div className="flex flex-col min-w-0">
            <span className="truncate text-sm font-medium">{label}</span>
            {website && (
              <span className="truncate text-xs mt-0.5" style={{ color: textColor }}>
                {website.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "")}
              </span>
            )}
          </div>
        </div>
      </div>
    </components.Option>
  );
};

const CustomInput = ({ children, ...props }: ControlProps<SourceCompanyOption, false, any>) => {
  return (
    <components.Control {...props}>
      <DropdownIndicator />
      {children}
    </components.Control>
  );
};

export const SourceCompanySelect = ({
  value = null,
  options,
  placeholder,
  isLoading = false,
  debouncedSearch,
  onValueChange,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
}: SourceCompanySelectProps) => {
  const { isDarkMode } = useSystemStore();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState<SourceCompanyOption | null>(value);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  // Capped like the Coverage Team dropdown — the raw space below grows as the sidebar scrolls.
  const maxMenuHeight = Math.min(useMaxMenuHeight(dropdownButtonRef), MAX_MENU_HEIGHT);
  const selectStyles = getCustomStyles(isDarkMode, maxMenuHeight);

  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  useEffect(() => {
    if (isOpen) {
      window.setTimeout(() => {
        const input = document.querySelector<HTMLInputElement>(
          ".select__control--menu-is-open .select__input"
        );
        if (input) {
          try {
            input.focus({ preventScroll: true });
          } catch {
            input.focus();
          }
        }
      }, 0);
    }
  }, [isOpen]);

  const handleOnChange = (newValue: SourceCompanyOption | null) => {
    setIsOpen(false);
    setSelectedValue(newValue);
    onValueChange(newValue);
  };

  const Placeholder = () => {
    if (selectedValue) {
      return (
        <div className="flex items-center gap-2 px-2 py-1 min-w-0">
          <CompanyIconBadge url={selectedValue.logo_url} />
          <span className="truncate text-sm font-medium max-w-[180px]">
            {selectedValue.label}
          </span>
        </div>
      );
    }

    return <p className="text-sm text-[#9CA1AF] text-overflow-ellipsis px-2 py-1">{placeholder}</p>;
  };

  return (
    <div>
      <Dropdown
        isDarkMode={isDarkMode}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        target={
          <div className="relative w-full">
            <div
              ref={dropdownButtonRef}
              className="flex items-center w-full min-h-[40px] h-[40px] rounded-lg border px-3 pr-8 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen((prev) => !prev);
              }}
              style={{
                borderColor: isDarkMode ? APP_COLORS.stroke.primaryDark : APP_COLORS.gray["200"],
                backgroundColor: isDarkMode ? APP_COLORS.background.primaryDark : "#fff",
                color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
                fontSize: 14,
              }}
            >
              <div className="flex-1 min-w-0 flex items-center h-full overflow-hidden">
                <Placeholder />
              </div>
              {selectedValue && (
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOnChange(null);
                    setIsOpen(false);
                  }}
                  className="flex items-center h-full ml-2"
                >
                  <div className="cursor-pointer opacity-70 duration-200 hover:opacity-100 flex items-center h-full">
                    <CloseRoundedIcon width={16} height={16} color="#6B7080" />
                  </div>
                </span>
              )}
            </div>
            <div className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none flex items-center">
              <SelectorIcon width={20} height={20} color={APP_COLORS.content.disabled} />
            </div>
          </div>
        }
      >
        <Select<SourceCompanyOption, false, GroupBase<SourceCompanyOption>>
          value={selectedValue}
          classNamePrefix="select"
          autoFocus={false}
          controlShouldRenderValue={false}
          hideSelectedOptions={false}
          isSearchable={true}
          components={{
            DropdownIndicator: null,
            IndicatorSeparator: null,
            Option: (props) => <CustomOption optionProps={props} />,
            Control: CustomInput,
          }}
          menuIsOpen={isOpen}
          menuPlacement="auto"
          menuPortalTarget={null}
          filterOption={null}
          styles={{
            ...selectStyles,
            menuList: (provided) => ({
              ...provided,
              borderTop: "1px #E5E7EB solid",
              width: "100%",
              overflowY: "auto",
              overflowX: "hidden",
              paddingTop: 0,
              paddingBottom: 0,
              maxHeight: maxMenuHeight,
              maxWidth: "100%",
            }),
          }}
          options={options || []}
          isLoading={isLoading}
          placeholder="Search"
          onChange={handleOnChange}
          onInputChange={debouncedSearch}
          onMenuScrollToBottom={() => {
            if (hasNextPage && !isFetchingNextPage) {
              void fetchNextPage();
            }
          }}
        />
      </Dropdown>
    </div>
  );
};

const Dropdown = ({
  children,
  isDarkMode,
  isOpen,
  target,
  onClose,
}: {
  children?: ReactNode;
  readonly isDarkMode: boolean;
  readonly isOpen: boolean;
  readonly target: ReactNode;
  readonly onClose: () => void;
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number | undefined>(undefined);

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && refs.reference.current instanceof HTMLElement) {
      setDropdownWidth(refs.reference.current.offsetWidth);
    }
  }, [isOpen, refs.reference.current]);

  return (
    <div ref={dropdownRef}>
      <div ref={refs.setReference}>{target}</div>
      {isOpen ? (
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            zIndex: 9999,
            width: dropdownWidth ? `${dropdownWidth}px` : "100%",
            backgroundColor: isDarkMode ? APP_COLORS.background.primaryDark : "#fff",
            border: `1px solid ${isDarkMode ? APP_COLORS.stroke.primaryDark : APP_COLORS.gray["200"]}`,
            color: isDarkMode ? APP_COLORS.text.primaryDark : APP_COLORS.text.primary,
          }}
          className="menu left-5 rounded-lg overflow-x-hidden max-w-full"
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

const DropdownIndicator = () => (
  <div className="dropdown-indicator flex items-center justify-center">
    <SearchIcon width={18} height={18} className="ml-2 mr-1 p-0 text-gray-400" />
  </div>
);
